import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { getLanguageObject } from '../../../redux/reducers/clientUserId.reducer';
import { useSelector } from 'react-redux';

// ─── Additional styles for Credits Modal ─────────────────────────────────────
const creditsModalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');

  :root {
    --cm-gold:      #f59e0b;
    --cm-gold-lt:   #fef3c7;
    --cm-gold-dk:   #d97706;
    --cm-bg:        #ffffff;
    --cm-overlay:   rgba(0,0,0,0.45);
    --cm-radius:    22px;
    --cm-shadow:    0 24px 60px rgba(0,0,0,0.22);
  }

  /* Overlay */
  .creditsModal__overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: var(--cm-overlay);
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(3px);
    animation: creditsModal__fadeIn 0.25s ease forwards;
  }
  .creditsModal__overlay--exit {
    animation: creditsModal__fadeOut 0.3s ease forwards;
  }

  /* Card */
  .creditsModal__card {
    position: relative;
    background: var(--cm-bg);
    border-radius: var(--cm-radius);
    box-shadow: var(--cm-shadow);
    padding: 2.5rem 2rem 2rem;
    max-width: 360px;
    width: 90%;
    text-align: center;
    overflow: hidden;
    animation: creditsModal__slideUp 0.35s cubic-bezier(.22,1,.36,1) forwards;
    font-family: 'Nunito', sans-serif;
  }
  .creditsModal__card--exit {
    animation: creditsModal__slideDown 0.28s ease forwards;
  }

  /* Top golden accent bar */
  .creditsModal__topbar {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 5px;
    background: linear-gradient(90deg, var(--cm-gold-dk), var(--cm-gold), #fcd34d, var(--cm-gold), var(--cm-gold-dk));
    background-size: 200% 100%;
    animation: creditsModal__shimmer 2.5s linear infinite;
    border-radius: var(--cm-radius) var(--cm-radius) 0 0;
    z-index: 2;
  }

  /* Trophy icon wrap */
  .creditsModal__trophy-wrap {
    width: 78px;
    height: 78px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.1rem;
    background: var(--cm-gold-lt);
    border: 3px solid var(--cm-gold);
    animation: creditsModal__pop 0.55s cubic-bezier(.22,1,.36,1) 0.15s both;
    position: relative;
    z-index: 1;
  }

  /* Trophy SVG */
  .creditsModal__trophy {
    width: 40px;
    height: 40px;
    color: var(--cm-gold-dk);
    filter: drop-shadow(0 2px 4px rgba(217,119,6,0.35));
  }

  /* "Congratulations!" label */
  .creditsModal__congrats {
    font-size: 0.78rem;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--cm-gold-dk);
    margin-bottom: 0.45rem;
    animation: creditsModal__fadeSlide 0.5s ease 0.35s both;
  }

  /* Credits amount */
  .creditsModal__amount {
    font-size: 2.5rem;
    font-weight: 900;
    color: #111827;
    line-height: 1;
    margin-bottom: 0.3rem;
    animation: creditsModal__countUp 0.65s cubic-bezier(.22,1,.36,1) 0.4s both;
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 0.35rem;
  }
  .creditsModal__amount-label {
    font-size: 1rem;
    font-weight: 700;
    color: #6b7280;
  }

  /* Subtitle */
  .creditsModal__subtitle {
    font-size: 0.88rem;
    color: #6b7280;
    margin-bottom: 1.6rem;
    font-weight: 600;
    animation: creditsModal__fadeSlide 0.5s ease 0.5s both;
  }

  /* Divider */
  .creditsModal__divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, #e5e7eb, transparent);
    margin: 0 -0.5rem 1.4rem;
    animation: creditsModal__fadeSlide 0.5s ease 0.55s both;
  }

  /* CTA button */
  .creditsModal__btn {
    width: 100%;
    padding: 0.8rem 1rem;
    border: none;
    border-radius: 14px;
    font-family: 'Nunito', sans-serif;
    font-size: 1rem;
    font-weight: 800;
    color: #fff;
    cursor: pointer;
    background: linear-gradient(135deg, var(--primary-blue), var(--material-dark));
    box-shadow: 0 4px 14px rgba(245,158,11,0.4);
    transition: box-shadow 0.2s, transform 0.15s;
    animation: creditsModal__fadeSlide 0.5s ease 0.6s both;
  }
  .creditsModal__btn:hover {
    box-shadow: 0 6px 20px rgba(245,158,11,0.55);
    transform: scale(1.02);
  }
  .creditsModal__btn:active { transform: scale(0.98); }

  /* Confetti canvas */
  .creditsModal__confetti {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
  }

  /* Content above confetti */
  .creditsModal__content { position: relative; z-index: 1; }

  /* Keyframes */
  @keyframes creditsModal__fadeIn  { from{opacity:0} to{opacity:1} }
  @keyframes creditsModal__fadeOut { from{opacity:1} to{opacity:0} }
  @keyframes creditsModal__slideUp {
    from { opacity:0; transform:translateY(40px) scale(0.95); }
    to   { opacity:1; transform:translateY(0)    scale(1);    }
  }
  @keyframes creditsModal__slideDown {
    from { opacity:1; transform:translateY(0)    scale(1);    }
    to   { opacity:0; transform:translateY(30px) scale(0.95); }
  }
  @keyframes creditsModal__pop {
    0%   { transform: scale(0)   rotate(-15deg); opacity:0; }
    60%  { transform: scale(1.2) rotate(5deg);  opacity:1; }
    80%  { transform: scale(0.95) rotate(-2deg); }
    100% { transform: scale(1)   rotate(0deg);  }
  }
  @keyframes creditsModal__countUp {
    from { opacity:0; transform: translateY(14px) scale(0.8); }
    to   { opacity:1; transform: translateY(0)    scale(1);   }
  }
  @keyframes creditsModal__fadeSlide {
    from { opacity:0; transform: translateY(8px); }
    to   { opacity:1; transform: translateY(0);   }
  }
  @keyframes creditsModal__shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

// ─── Confetti (reused pattern, gold-tinted palette) ──────────────────────────
function useCreditsConfetti(canvasRef, active) {
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const COLORS = ['#f59e0b', '#fcd34d', '#d97706', '#10b981', '#1a6cf6', '#ec4899', '#8b5cf6'];
    const pieces = Array.from({ length: 70 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height * 0.4 - canvas.height * 0.2,
      w: 6 + Math.random() * 8,
      h: 4 + Math.random() * 5,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      rotation: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.15,
      vx: (Math.random() - 0.5) * 2,
      vy: 1.5 + Math.random() * 2.5,
      alpha: 1,
      // some star-shaped: if rand < 0.2, draw circle instead
      isCircle: Math.random() < 0.2,
    }));

    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pieces.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotSpeed;
        if (p.y > canvas.height * 0.82) p.alpha -= 0.022;
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.alpha);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;
        if (p.isCircle) {
          ctx.beginPath();
          ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
        ctx.restore();
      });
      if (pieces.some((p) => p.alpha > 0)) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [active, canvasRef]);
}

// ─── Trophy SVG Icon ─────────────────────────────────────────────────────────
const TrophyIcon = () => (
  <svg
    className='creditsModal__trophy'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.8'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    {/* Cup body */}
    <path d='M8 21h8' />
    <path d='M12 17v4' />
    <path d='M7 4H4a1 1 0 0 0-1 1v2a4 4 0 0 0 4 4' />
    <path d='M17 4h3a1 1 0 0 1 1 1v2a4 4 0 0 1-4 4' />
    {/* Main cup */}
    <path d='M7 4a5 5 0 0 0 5 9 5 5 0 0 0 5-9H7z' fill='currentColor' fillOpacity='0.15' />
    <path d='M7 4h10' />
    <path d='M9 13c0 1.657 1.343 4 3 4s3-2.343 3-4' />
  </svg>
);

// ─── Credits Modal Core ───────────────────────────────────────────────────────
function CreditsModalCore({ config, onClose, languageObject }) {
  const [exiting, setExiting] = useState(false);
  const canvasRef = useRef(null);

  const {
    credits, // required: number of credits received
    creditLabel = languageObject.credits || 'Credits', // label shown next to the number
    subtitle = '', // subtext below amount
    btnText = languageObject.continue + '!' || 'Continue!',
  } = config;

  useCreditsConfetti(canvasRef, true);

  const handleClose = useCallback(() => {
    setExiting(true);
    setTimeout(onClose, 280);
  }, [onClose]);

  return (
    <div
      className={`creditsModal__overlay${exiting ? ' creditsModal__overlay--exit' : ''}`}
      onClick={handleClose}
      role='dialog'
      aria-modal='true'
      aria-label='Credits received'
    >
      <div
        className={`creditsModal__card${exiting ? ' creditsModal__card--exit' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Golden shimmer top bar */}
        <div className='creditsModal__topbar' />

        {/* Confetti canvas */}
        <canvas ref={canvasRef} className='creditsModal__confetti' />

        <div className='creditsModal__content'>
          {/* Trophy */}
          <div className='creditsModal__trophy-wrap'>
            <TrophyIcon />
          </div>

          {/* Congratulations text */}
          <div className='creditsModal__congrats'>
            🎉 {languageObject.congratulations || 'Congratulations'}!
          </div>

          {/* Credits amount */}
          <div className='creditsModal__amount'>
            +{credits}
            <span className='creditsModal__amount-label'>{creditLabel}</span>
          </div>

          {/* Subtitle */}
          {subtitle && <p className='creditsModal__subtitle'>{subtitle}</p>}

          <div className='creditsModal__divider' />

          {/* CTA */}
          <button className='creditsModal__btn' onClick={handleClose}>
            {btnText}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Context + Provider ───────────────────────────────────────────────────────
const CreditsModalCtx = createContext(null);

export function CreditsModalProvider({ children }) {
  const [modalConfig, setModalConfig] = useState(null);

  const showCreditsModal = useCallback((config) => setModalConfig(config), []);
  const hideCreditsModal = useCallback(() => setModalConfig(null), []);

  const languageObject = useSelector((state) => getLanguageObject(state));

  return (
    <CreditsModalCtx.Provider value={{ showCreditsModal }}>
      <style>{creditsModalStyles}</style>
      {children}
      {modalConfig && (
        <CreditsModalCore
          config={modalConfig}
          onClose={hideCreditsModal}
          languageObject={languageObject}
        />
      )}
    </CreditsModalCtx.Provider>
  );
}

/** Hook — call showCreditsModal({ credits, ... }) from any child */
export function useCreditsModal() {
  const ctx = useContext(CreditsModalCtx);
  if (!ctx) throw new Error('useCreditsModal must be used inside <CreditsModalProvider>');
  return ctx.showCreditsModal;
}
